// An optimistic (OCC) transaction over the MVCC store.
//
// A transaction takes a stable snapshot of memory at `readVersion` (the store's
// committed version when it began). Reads see that snapshot plus the
// transaction's own buffered writes; writes are buffered and only land in the
// store at commit. At commit we *validate*: if any address the transaction read
// has a newer committed version than our snapshot, someone else changed data we
// depended on, so we abort and the caller retries.

import { Store, VersionedEntry } from "./store.js";
import { Address, StoredObject } from "./value.js";

export class Transaction {
  /** Snapshot version: the newest committed version visible to this txn. */
  readonly readVersion: number;
  /** Buffered writes (null == tombstone / delete). Applied atomically at commit. */
  readonly writes = new Map<Address, StoredObject | null>();
  /** Addresses whose visible version this txn depended on. */
  readonly readSet = new Set<Address>();

  constructor(readonly id: number, private store: Store) {
    this.readVersion = store.committedVersion;
  }

  /** Read `addr`, recording the dependency. Own buffered writes win. */
  readEntry(addr: Address): VersionedEntry | undefined {
    this.readSet.add(addr);
    if (this.writes.has(addr)) {
      const buffered = this.writes.get(addr)!;
      if (buffered === null) return undefined;
      return { begin: this.id, end: null, value: buffered };
    }
    return this.store.read(addr, this.readVersion);
  }

  write(addr: Address, value: StoredObject | null): void {
    this.writes.set(addr, value);
  }

  /**
   * Direct children of `prefix` live for this txn: committed children at the
   * snapshot, overlaid with buffered adds (non-null) and removes (null).
   */
  liveChildren(prefix: Address): Address[] {
    const needle = prefix + "/";
    const result = new Set(this.store.liveChildren(prefix, this.readVersion));
    for (const [addr, value] of this.writes) {
      if (!addr.startsWith(needle)) continue;
      if (addr.indexOf("/", needle.length) !== -1) continue;
      if (value === null) result.delete(addr);
      else result.add(addr);
    }
    return [...result];
  }

  /**
   * Validate the read set against the current store. Returns the first stale
   * address (a conflict), or null if the transaction may commit.
   */
  validate(): Address | null {
    for (const addr of this.readSet) {
      if (this.store.latestBegin(addr) > this.readVersion) return addr;
    }
    return null;
  }

  /** Apply buffered writes atomically; returns the assigned commit version. */
  commit(): number {
    return this.store.commit(this.writes);
  }
}
